import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/layout/AppLayout';
import { Badge } from '@/components/ui/badge';
import {
  Search, AlertTriangle, Clock, ChevronRight, Users, Filter,
} from 'lucide-react';
import { patients as rawPatients } from '@/data/mockData';

/* ═══ ENRICHED MOCK ═══ */
const CM_NAMES = ['Dott.ssa L. Conti', 'Dott. M. Ferrara', 'Dott.ssa A. Romano', 'Dott. G. Neri'];
const SETTINGS = ['Bassa', 'Media', 'Alta'];
const PAI_STATUSES = ['Attivo', 'In Scadenza', 'Sospeso', 'Chiuso'];

const enrichedPatients = rawPatients.slice(0, 12).map((p, i) => ({
  ...p,
  setting: SETTINGS[i % 3],
  paiStatus: PAI_STATUSES[i % 4],
  paiDaysLeft: PAI_STATUSES[i % 4] === 'In Scadenza' ? 8 + (i % 12) : PAI_STATUSES[i % 4] === 'Attivo' ? 30 + (i % 60) : null,
  caseManager: CM_NAMES[i % 4],
  hasAlert: i % 5 === 0,
  erogatore: ['Coop. Salute Roma', 'Consorzio ADI Lazio', 'Fondazione Gemelli@Home', 'ASL Roma 2'][i % 4],
}));

const SETTING_COLORS = {
  Bassa: { bg: 'bg-emerald-50 dark:bg-emerald-950/30', text: 'text-emerald-700 dark:text-emerald-400' },
  Media: { bg: 'bg-amber-50 dark:bg-amber-950/30', text: 'text-amber-700 dark:text-amber-400' },
  Alta: { bg: 'bg-red-50 dark:bg-red-950/30', text: 'text-red-700 dark:text-red-400' },
};

const STATUS_COLORS = {
  Attivo: { bg: 'bg-emerald-50 dark:bg-emerald-950/30', text: 'text-emerald-700 dark:text-emerald-400', dot: 'bg-emerald-500' },
  'In Scadenza': { bg: 'bg-amber-50 dark:bg-amber-950/30', text: 'text-amber-700 dark:text-amber-400', dot: 'bg-amber-500' },
  Sospeso: { bg: 'bg-orange-50 dark:bg-orange-950/30', text: 'text-orange-700 dark:text-orange-400', dot: 'bg-orange-500' },
  Chiuso: { bg: 'bg-slate-100 dark:bg-slate-700/40', text: 'text-slate-600 dark:text-slate-300', dot: 'bg-slate-400' },
};

/* ═══ FILTER PILLS ═══ */
const PILLS_COMPLEXITY = ['Tutti', 'Bassa', 'Media', 'Alta'];
const PILLS_STATUS = ['Tutti', 'Attivo', 'In Scadenza', 'Sospeso', 'Chiuso'];

export default function ArchivioPazientiMedico() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filterComplexity, setFilterComplexity] = useState('Tutti');
  const [filterStatus, setFilterStatus] = useState('Tutti');
  const [filterAlerts, setFilterAlerts] = useState(false);

  const filtered = useMemo(() => {
    return enrichedPatients.filter(p => {
      if (search) {
        const q = search.toLowerCase();
        const fullName = `${p.cognome} ${p.nome}`.toLowerCase();
        if (!fullName.includes(q) && !p.codiceFiscale.toLowerCase().includes(q)) return false;
      }
      if (filterComplexity !== 'Tutti' && p.setting !== filterComplexity) return false;
      if (filterStatus !== 'Tutti' && p.paiStatus !== filterStatus) return false;
      if (filterAlerts && !p.hasAlert) return false;
      return true;
    });
  }, [search, filterComplexity, filterStatus, filterAlerts]);

  return (
    <AppLayout role="medico">
      <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-5" data-testid="archivio-pazienti-page">
        {/* Header */}
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-white" data-testid="archivio-title">Archivio Pazienti</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Fascicolo Clinico Elettronico - Pazienti in carico</p>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
            <Users className="w-4 h-4" />
            <span><strong className="text-foreground">{filtered.length}</strong> di {enrichedPatients.length} pazienti</span>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-3" data-testid="toolbar">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Cerca per nome, cognome o codice fiscale..."
              className="w-full h-10 pl-10 pr-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm text-foreground placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 transition-colors"
              data-testid="search-input"
            />
          </div>

          {/* Filter Pills */}
          <div className="flex flex-wrap items-center gap-4">
            {/* Complexity */}
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mr-1">Complessita:</span>
              {PILLS_COMPLEXITY.map(p => (
                <button
                  key={p}
                  onClick={() => setFilterComplexity(p)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors
                    ${filterComplexity === p
                      ? 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-400 ring-1 ring-cyan-300 dark:ring-cyan-700'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'}`}
                  data-testid={`pill-complexity-${p.toLowerCase()}`}
                >
                  {p}
                </button>
              ))}
            </div>

            <div className="w-px h-5 bg-slate-200 dark:bg-slate-700" />

            {/* Status */}
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mr-1">Stato PAI:</span>
              {PILLS_STATUS.map(s => (
                <button
                  key={s}
                  onClick={() => setFilterStatus(s)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors
                    ${filterStatus === s
                      ? 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-400 ring-1 ring-cyan-300 dark:ring-cyan-700'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'}`}
                  data-testid={`pill-status-${s.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {s}
                </button>
              ))}
            </div>

            <div className="w-px h-5 bg-slate-200 dark:bg-slate-700" />

            {/* Alert toggle */}
            <button
              onClick={() => setFilterAlerts(v => !v)}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors
                ${filterAlerts
                  ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 ring-1 ring-red-300 dark:ring-red-700'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'}`}
              data-testid="pill-alerts"
            >
              <AlertTriangle className="w-3 h-3" /> Solo Alert
            </button>
          </div>
        </div>

        {/* Patient Grid */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden" data-testid="patient-grid">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700/60">
                {['Paziente', 'Complessita', 'Stato PAI', 'Scadenza', 'Case Manager', 'Azioni'].map(col => (
                  <th key={col} className="px-5 py-3 text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-slate-400">Nessun paziente trovato.</td></tr>
              ) : filtered.map(p => {
                const sc = STATUS_COLORS[p.paiStatus] || STATUS_COLORS.Chiuso;
                const settC = SETTING_COLORS[p.setting] || SETTING_COLORS.Bassa;
                const daysColor = p.paiDaysLeft != null && p.paiDaysLeft <= 14 ? 'text-red-600 dark:text-red-400 font-semibold' : p.paiDaysLeft != null && p.paiDaysLeft <= 30 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-500 dark:text-slate-400';
                return (
                  <tr key={p.id} className="border-b border-slate-100 dark:border-slate-800/60 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors" data-testid={`patient-row-${p.id}`}>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        {p.hasAlert && <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />}
                        <div>
                          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{p.cognome} {p.nome}</p>
                          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{p.eta} anni · {p.codiceFiscale.substring(0, 6)}...</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <Badge className={`${settC.bg} ${settC.text} border-0 text-[10px] font-semibold px-2.5 py-0.5`}>{p.setting}</Badge>
                    </td>
                    <td className="px-5 py-3.5">
                      <Badge className={`${sc.bg} ${sc.text} border-0 text-[10px] font-semibold gap-1.5 px-2.5 py-0.5`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                        {p.paiStatus}
                      </Badge>
                    </td>
                    <td className="px-5 py-3.5">
                      {p.paiDaysLeft != null ? (
                        <div className="flex items-center gap-1.5">
                          <Clock className={`w-3.5 h-3.5 ${daysColor}`} />
                          <span className={`text-xs ${daysColor}`}>{p.paiDaysLeft} gg</span>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-xs text-slate-600 dark:text-slate-300">{p.caseManager}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <button
                        onClick={() => navigate(`/medico/pazienti/${p.id}`)}
                        className="flex items-center gap-1.5 text-xs font-medium text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 transition-colors"
                        data-testid={`open-fascicolo-${p.id}`}
                      >
                        Fascicolo <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </AppLayout>
  );
}
